import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../utils/auth';
import { getAuthToken } from "../utils/auth";

import { useAuth } from "../hooks/useAuth";
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import DadosPessoais from '../components/dashboard/DadosPessoais';
import Pagamentos from '../components/dashboard/Pagamentos';
import Consultas from '../components/dashboard/Consultas';
import ApoioCliente from '../components/dashboard/ApoioCliente';


type TabType = 'dados' | 'pagamentos' | 'consultas' | 'apoio';


const UserDashboardPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>('dados');
    const [, setShowLoginModal] = useState(false);
    const [credits, setCredits] = useState(0);

    const { isAuthenticated, user, isLoading } = useAuth();

    // Fetch credits when user is authenticated
    useEffect(() => {
        const fetchCredits = async () => {
            const token = getAuthToken();
            if (!token || !isAuthenticated) {
                setCredits(0);
                return;
            }

            try {
                const creditsResponse = await fetch('/api/credits/me', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (creditsResponse.ok) {
                    const creditsData = await creditsResponse.json();
                    setCredits(creditsData.credits || 0);
                } else {
                    setCredits(0);
                }
            } catch (error) {
                console.error('Error fetching credits:', error);
                setCredits(0);
            }
        };

        if (isAuthenticated && !isLoading) {
            fetchCredits();
        }
    }, [isAuthenticated, isLoading]);

    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const tabs = [
        { id: 'dados' as TabType, label: 'Dados Pessoais', icon: '👤' },
        { id: 'consultas' as TabType, label: 'Consultas', icon: '🔮' },
        { id: 'pagamentos' as TabType, label: 'Pagamentos', icon: '💳' },
        { id: 'apoio' as TabType, label: 'Apoio ao Cliente', icon: '💬' },
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'dados':
                return <DadosPessoais />;
            case 'pagamentos':
                return <Pagamentos />;
            case 'consultas':
                return <Consultas />;
            case 'apoio':
                return <ApoioCliente />;
            // default:
            //     return <DadosPessoais />;
        }
    };

    return (
        <div className="min-h-screen bg-[#0b1220] text-[#e5e7eb]">
            <Header
                isAuthenticated={isAuthenticated}
                userName={user?.name || ''}
                credits={credits}
                onOpenLogin={() => setShowLoginModal(true)}
                onLogout={handleLogout}
            />

            {/* Main Content */}
            <main className="max-w-[1120px] mx-auto px-3 sm:px-4 py-6 sm:py-8">
                {/* Welcome Section */}
                <div className="mb-4 sm:mb-6">
                    <h2 className="text-[1.3rem] sm:text-[1.6rem] font-bold text-[#f9fafb] mb-1">Minha Conta</h2>
                    <p className="text-[#9ca3af] text-sm">Gerencie suas informações e consultas</p>
                </div>

                {/* Tabs Navigation */}
                <div className="mb-4 sm:mb-6 border-b border-[#1f2937]">
                    <nav className="flex gap-1 sm:gap-6 overflow-x-auto scrollbar-hide -mb-px" aria-label="Tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                                    whitespace-nowrap py-2.5 sm:py-3 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm
                                    transition-colors duration-200 flex items-center gap-1.5 sm:gap-2 flex-shrink-0
                                    ${activeTab === tab.id
                                        ? 'border-[#8b5cf6] text-[#f9fafb]'
                                        : 'border-transparent text-[#9ca3af] hover:text-[#e5e7eb] hover:border-[#374151]'
                                    }
                                `}
                            >
                                <span className="text-base sm:text-lg">{tab.icon}</span>
                                <span className="hidden sm:inline">{tab.label}</span>
                                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="mb-8">
                    <div className="rounded-xl p-3 sm:p-6 shadow-[0_8px_20px_rgba(2,6,23,0.4)]">
                        {renderTabContent()}
                    </div>
                </div>
            </main>

            <Footer />

        </div>
    );
};

export default UserDashboardPage;
